import useSWR, { SWRConfiguration, SWRResponse } from 'swr'
import { GrpcWebClientBase, Metadata } from 'grpc-web'
import * as google_protobuf_wrappers_pb from 'google-protobuf/google/protobuf/wrappers_pb'
import { useContext, useCallback, useEffect, useMemo, useRef } from 'react'
import {
  AuthRecord,
  useAuth,
  useAuthContext,
  useAuthHeader,
} from '../../context/authContext'
import {
  getAuthorizationCacheScope,
  getRpcAccess,
  isPermissionDenied,
  RpcAccessDenied,
  RpcAccess,
} from '../../app/AppAuth/rpcAuthorization'
import { interceptTokenErrors, NTRCE_API_URL } from '../../adapter/gRpcAdapter'
import { createRpcSession, useCurrentAuth } from '../../adapter/rpcSession'
import { stringValue } from '../../Utils'
import { AuditContext } from '../../context/AuditContext'
import { getRefererHeader } from '../../adapter/referer'
import { getSwrRequestIdHeader } from '../../adapter/requestIdHeader'
import { GrpcCall, useGetCacheKey } from './utils'
import { queue, QUEUE_PRIORITY } from './queue'

export async function callGrpc<Request, Response>(
  asyncCall: GrpcCall<Request, Response>,
  request: Request,
  header: Metadata,
  auth: AuthRecord,
): Promise<Response> {
  if (getRpcAccess(asyncCall, auth, request) !== 'allowed')
    return Promise.reject(new RpcAccessDenied(asyncCall))
  return asyncCall.call(
    {
      client_: new GrpcWebClientBase({ format: 'text' }),
      hostname_: NTRCE_API_URL,
    },
    request,
    {
      ...header,
      ...getSwrRequestIdHeader(header),
    },
  )
}

// For imperative reads/writes outside SWR. Saved callbacks cannot dispatch or
// publish results after their user, account, or permission snapshot changes.
export function useGrpcCall(): <Request, Response>(
  method: GrpcCall<Request, Response>,
  request: Request,
  header: Metadata,
) => Promise<Response> {
  const { authCtx: auth, authDispatch } = useAuthContext()
  const currentAuth = useCurrentAuth(auth)
  return useCallback(
    async <Request, Response>(
      method: GrpcCall<Request, Response>,
      request: Request,
      header: Metadata,
    ) => {
      const session = createRpcSession(auth, currentAuth, method, request)
      if (!session.isCurrent()) throw new RpcAccessDenied(method)
      const response = await interceptTokenErrors(
        auth,
        authDispatch,
        (tokenHeader: Metadata) => {
          if (!session.isCurrent()) throw new RpcAccessDenied(method)
          return callGrpc(
            method,
            request,
            { ...header, ...tokenHeader },
            session.auth as AuthRecord,
          ).catch((error: unknown) => {
            if (!session.isCurrent() && !session.canRecover(error as Error))
              throw new RpcAccessDenied(method)
            throw error
          })
        },
        session.acceptRefresh,
      )
      if (!session.isCurrent()) throw new RpcAccessDenied(method)
      return response
    },
    [auth, authDispatch, currentAuth],
  )
}

export type AuthorizedQueryState = { access: RpcAccess; isDisabled: boolean }
export type AuthorizedSwrResponse<Response> = SWRResponse<Response> &
  AuthorizedQueryState

export type SwrGrpcResponse = SWRConfiguration & {
  ignoreAuditContext?: boolean
  persistSize?: boolean
  revalidateFirstPage?: boolean
  headers?: Metadata
  // p-queue priority — see QUEUE_PRIORITY. Set this on calls that gate a page's main
  // content so they aren't stuck behind the rest of the page's fetches.
  priority?: number
}

// Helper hook for executing a GRPC request with the SWR library.
export function useSwrGrpc<Request, Response>(
  asyncCall: GrpcCall<Request, Response>,
  request: Request,
  shouldFetch = true,
  config?: SwrGrpcResponse,
): { response: AuthorizedSwrResponse<Response>; cacheKey: unknown[] } {
  const cacheKey = useGetCacheKey(asyncCall, request)
  const auth = useAuth()
  const access = getRpcAccess(asyncCall, auth, request)
  const enabled = shouldFetch && access === 'allowed'
  const scope = getAuthorizationCacheScope(auth)
  const current = useRef({ auth, enabled, scope })
  current.current = { auth, enabled, scope }
  useEffect(() => {
    current.current.enabled = enabled
    return () => {
      current.current.enabled = false
    }
  }, [enabled])
  const baseHeader = useAuthHeader()
  const { auditId } = useContext(AuditContext)
  const contextualisedRequest = useMemo(() => {
    if (config?.ignoreAuditContext) {
      return request
    }
    if (!isAuditDependent(request) || !auditId) {
      return request
    }

    // Allow user to set their own audit id and don't override it
    if (request.getAuditId()?.getValue() !== undefined) {
      return request
    }

    return request.setAuditId(stringValue(auditId))
  }, [auditId, config?.ignoreAuditContext, request])

  const headers = useMemo(() => {
    return {
      ...baseHeader,
      ...config?.headers,
      ...getRefererHeader(),
    }
  }, [baseHeader, config?.headers])

  const response = useSWR<Response>(
    enabled ? cacheKey : null,
    () =>
      queue.add(
        async () => {
          if (
            !current.current.enabled ||
            current.current.scope !== scope ||
            getRpcAccess(
              asyncCall,
              current.current.auth,
              contextualisedRequest,
            ) !== 'allowed'
          )
            throw new RpcAccessDenied(asyncCall)
          const data = await callGrpc(
            asyncCall,
            contextualisedRequest,
            headers,
            current.current.auth,
          ).catch((error: unknown) => {
            if (!current.current.enabled || current.current.scope !== scope)
              throw new RpcAccessDenied(asyncCall)
            throw error
          })
          if (!current.current.enabled || current.current.scope !== scope)
            throw new RpcAccessDenied(asyncCall)
          return data
        },
        {
          priority: config?.priority ?? QUEUE_PRIORITY.DEFAULT,
        },
      ) as Promise<Response>,
    {
      ...config,
      keepPreviousData: false,
      shouldRetryOnError: (error) => {
        if (
          isPermissionDenied(error) ||
          !current.current.enabled ||
          current.current.scope !== scope
        )
          return false
        return typeof config?.shouldRetryOnError === 'function'
          ? config.shouldRetryOnError(error)
          : config?.shouldRetryOnError !== false
      },
    },
  )
  const { mutate: swrMutate } = response
  const mutate = useCallback(
    (...args: Parameters<typeof swrMutate>) => {
      if (!current.current.enabled || current.current.scope !== scope)
        return Promise.resolve(undefined)
      return swrMutate(...args)
    },
    [swrMutate, scope],
  ) as typeof swrMutate
  const guardedResponse = {
    ...response,
    data: enabled ? response.data : undefined,
    // A skipped read is an authorization state, not a failed network request.
    // Keep hook-level error effects and toasts reserved for attempted requests.
    access,
    isDisabled: !shouldFetch || access === 'denied',
    error: enabled ? response.error : undefined,
    isLoading:
      shouldFetch && access === 'pending'
        ? true
        : enabled && response.isLoading,
    isValidating: enabled && response.isValidating,
    mutate,
  }
  return { response: guardedResponse, cacheKey }
}

type AuditDependent<T> = T & {
  getAuditId(): google_protobuf_wrappers_pb.StringValue | undefined
  setAuditId(value?: google_protobuf_wrappers_pb.StringValue): AuditDependent<T>
}

export function isAuditDependent<T>(p: T): p is AuditDependent<T> {
  if (
    p instanceof Object &&
    !!(p as Record<string, unknown>)['setAuditId'] &&
    !!(p as Record<string, unknown>['getAuditId'])
  ) {
    const request = p as AuditDependent<T>
    return typeof request.getAuditId() !== 'string'
  }

  return false
}
